import React, { useState, useEffect } from "react";
import { Wifi, Menu, X, Check, ChevronDown } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Navbar() {
  const { t, lang, setLang, languages } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { key: "how", href: "#come-funziona" },
    { key: "cards", href: "#card" },
    { key: "coverage", href: "#copertura" },
    { key: "news", href: "#novita" },
    { key: "contact", href: "#contatti" },
  ];

  const current = languages.find((l) => l.code === lang);

  const handleNav = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "hsc-glass shadow-[0_8px_30px_-18px_rgba(13,100,120,0.4)] border-b border-white/40" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <a href="#top" onClick={(e) => { e.preventDefault(); handleNav("#top"); }} className="flex items-center gap-3 group">
          <span className="relative grid place-items-center w-12 h-12 rounded-2xl bg-primary text-white shadow-[0_10px_24px_-8px_rgba(13,148,168,0.9)] group-hover:scale-105 transition-transform">
            <Wifi className="w-7 h-7" strokeWidth={2.6} />
          </span>
          <span className="leading-none">
            <span className="block font-display font-extrabold text-[22px] text-slate-900 tracking-tight">Hotspot</span>
            <span className="block text-[12px] font-bold tracking-[0.28em] text-primary uppercase mt-1">Costarei</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.key}
              onClick={() => handleNav(item.href)}
              className="px-3.5 py-2 text-[14px] font-medium text-slate-700 hover:text-primary rounded-lg hover:bg-primary/5 transition-colors"
            >
              {t.nav[item.key]}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[13px] font-semibold text-slate-700 hover:bg-primary/5 border border-slate-200/70 transition-colors">
                <span className="text-primary">{current?.flag}</span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <span className="flex items-center gap-2.5">
                    <span className="text-xs font-bold text-primary w-6">{l.flag}</span>
                    <span className="text-sm">{l.label}</span>
                  </span>
                  {lang === l.code && <Check className="w-4 h-4 text-primary" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            onClick={() => handleNav("#card")}
            className="hidden sm:inline-flex rounded-full px-5 font-semibold shadow-[0_10px_24px_-10px_rgba(13,148,168,0.9)]"
          >
            {t.nav.activate}
          </Button>

          <button
            className="lg:hidden grid place-items-center w-10 h-10 rounded-lg text-slate-800 hover:bg-primary/5"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-300 hsc-glass border-b border-white/40 ${
          open ? "max-h-96" : "max-h-0"
        }`}
      >
        <div className="px-5 py-4 flex flex-col gap-1">
          {navItems.map((item) => (
            <button
              key={item.key}
              onClick={() => handleNav(item.href)}
              className="text-left px-3 py-3 rounded-lg text-[15px] font-medium text-slate-800 hover:bg-primary/5"
            >
              {t.nav[item.key]}
            </button>
          ))}
          <Button onClick={() => handleNav("#card")} className="mt-2 rounded-full font-semibold">
            {t.nav.activate}
          </Button>
        </div>
      </div>
    </header>
  );
}
